// src/components/report_details/Whois/WhoisInfoSection.tsx
import React from "react";
import { WhoisInfoDetail } from "../../../types/interfaces";
import { useTranslation } from "react-i18next";

interface Props {
  whois: WhoisInfoDetail | null | undefined;
  selectedDomain: string;
  domains: { id: number; domain_name: string }[];
  onDomainChange: (d: string) => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode; note: string }> = ({
  title,
  children,
  note,
}) => (
  <div className="mb-8">
    <h4 className="font-semibold text-gray-600 mb-2">{title}</h4>
    <p className="text-sm text-gray-500 italic mb-2">{note}</p>
    <table className="min-w-full text-left bg-white border rounded-lg">
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value ? (
    <tr className="bg-gray-50 even:bg-gray-100 hover:bg-gray-200">
      <td className="py-2 px-4 border font-medium w-1/3">{label}</td>
      <td className="py-2 px-4 border">{value}</td>
    </tr>
  ) : null;

const WhoisInfoSection: React.FC<Props> = ({
  whois,
  selectedDomain,
  domains,
  onDomainChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="p-8 bg-white rounded-lg shadow-lg mb-6">
      {/* título */}
      <div className="flex items-center mb-6">
        <div className="w-3 h-10 bg-gray-800 mr-4" />
        <h3 className="text-4xl font-bold">{t("whois.title")}</h3>
      </div>

      {/* párrafo principal */}
      <p className="text-gray-700 mb-6 text-xl">
        {t("whois.paragraph_extended")}
      </p>

      {/* selector de dominio */}
      {domains.length > 1 && (
        <div className="mb-8">
          <label htmlFor="domain-select" className="block text-lg font-medium">
            <strong>{t("whois.select_domain")}</strong>
          </label>
          <select
            id="domain-select"
            value={selectedDomain}
            onChange={(e) => onDomainChange(e.target.value)}
            className="p-2 border rounded bg-gray-800 text-white font-bold w-64"
          >
            {domains.map((d) => (
              <option key={d.id} value={d.domain_name}>
                {d.domain_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* contenido */}
      {whois ? (
        <>
          <Section
            title={t("whois.domain_section")}
            note={t("whois.domain_note")}
          >
            <Row label={t("whois.creation")} value={whois.creation_date?.slice(0, 10)} />
            <Row label={t("whois.updated")} value={whois.updated_date?.slice(0, 10)} />
            <Row label={t("whois.expiry")} value={whois.registry_expiry_date?.slice(0, 10)} />
            <Row label={t("whois.status")} value={whois.domain_status} />
            <Row label={t("whois.registry_id")} value={whois.registry_domain_id} />
          </Section>

          <Section
            title={t("whois.registrar_section")}
            note={t("whois.registrar_note")}
          >
            <Row label={t("whois.registrar")} value={whois.registrar} />
            <Row label={t("whois.registrar_url")} value={whois.registrar_url} />
            <Row label={t("whois.whois_server")} value={whois.registrar_whois_server} />
            <Row label={t("whois.abuse_email")} value={whois.registrar_abuse_contact_email} />
            <Row label={t("whois.abuse_phone")} value={whois.registrar_abuse_contact_phone} />
          </Section>

          <Section
            title={t("whois.registrant_section")}
            note={t("whois.registrant_note")}
          >
            <Row label={t("whois.name")} value={whois.registrant_name} />
            <Row label={t("whois.organization")} value={whois.registrant_organization} />
            <Row label={t("whois.city")} value={whois.registrant_city} />
            <Row label={t("whois.country")} value={whois.registrant_country} />
            <Row label={t("whois.email")} value={whois.registrant_email} />
            <Row label={t("whois.phone")} value={whois.registrant_phone} />
          </Section>

          <Section
            title={t("whois.admin_section")}
            note={t("whois.admin_note")}
          >
            <Row label={t("whois.name")} value={whois.admin_name} />
            <Row label={t("whois.organization")} value={whois.admin_organization} />
            <Row label={t("whois.city")} value={whois.admin_city} />
            <Row label={t("whois.country")} value={whois.admin_country} />
            <Row label={t("whois.email")} value={whois.admin_email} />
            <Row label={t("whois.phone")} value={whois.admin_phone} />
          </Section>

          <Section
            title={t("whois.tech_section")}
            note={t("whois.tech_note")}
          >
            <Row label={t("whois.name")} value={whois.tech_name} />
            <Row label={t("whois.organization")} value={whois.tech_organization} />
            <Row label={t("whois.city")} value={whois.tech_city} />
            <Row label={t("whois.country")} value={whois.tech_country} />
            <Row label={t("whois.email")} value={whois.tech_email} />
            <Row label={t("whois.phone")} value={whois.tech_phone} />
          </Section>
        </>
      ) : (
        <p className="text-gray-500">{t("whois.no_data")}</p>
      )}
    </div>
  );
};

export default WhoisInfoSection;
